
// No easing
function none() {
  return 1;
}

// Linear
function linear(t: number) {
  return t;
}

// Flash style transition
// ease in [-1, 1] for usage similar to flash
// but works with ease in ]-Inf, +Inf[
function flash(t: number, ease: number) {
  return t + t * ease - t * t * ease;
}

// Parabolic
function parabolic(t: number) {
  let r = (2 * t - 1);
  return 1 - r * r;
}

// Trigonometric, n = number of iterations in ]-Inf, +Inf[
function trigo(t: number, n: number) {
  return 0.5 * (1 - Math.cos(Math.PI * 2 * t * n));
}

// Polynomial, p = power in ]0, + Inf[
function polyIn(t: number, p: number) { 
  return Math.pow(t, p);
}

function polyOut(t: number, p: number) {
  return 1 - Math.pow(1 - t, p);
}

function polyInOut(t: number, p: number) {
  if (t < 0.5) {
    return 0.5 * polyIn(2 * t, p);
  }
  return 0.5 + 0.5 * polyOut(2 * t - 1, p);
}

// Sine
function sineIn(t: number) {
  return 1 - Math.cos(Math.PI / 2 * t);
}

function sineOut(t: number) {
  return Math.sin(Math.PI / 2 * t);
}

function sineInOut(t: number) {
  if (t < 0.5) {
    return 0.5 * sineIn(2 * t);
  }
  return 0.5 + 0.5 * sineOut(2 * t - 1);
}

// Exponential, e = exponent in ]0, + Inf[
function expIn(t: number, e: number) {
  return (1 - Math.pow(Math.E, e * t)) / (1 - Math.pow(Math.E, e));
}

function expOut(t: number, e: number) {
  return (1 - Math.pow(Math.E, - e * t)) / (1 - Math.pow(Math.E, - e));
}

function expInOut(t: number, e: number) {
  if (t < 0.5) {
    return 0.5 * expIn(2 * t, e);
  }
  return 0.5 + 0.5 * expOut(2 * t - 1, e);
}

// Circular
function circIn(t: number) {
  return 1 - Math.sqrt(1 - t * t);
}

function circOut(t: number) {
  return Math.sqrt(1 - (1 - 2 * t + t * t));
}

function circInOut(t: number) {
  if (t < 0.5) {
    return 0.5 * circIn(2 * t);
  }
  return 0.5 + 0.5 * circOut(2 * t - 1);
}

// Elastic, e = elasticity in ]0, +Inf[
function elasticIn(t: number, e: number) {
  if (t === 0) {
    return 0;
  }
  e /= (e + 1); // transforming e
  const n = (1 + e) * Math.log(t) / Math.log(e);
  return Math.cos(n) * Math.pow(e, n);
}

function elasticOut(t: number, e: number) {
  if (t === 1) {
    return 1;
  }
  e /= (e + 1); // transforming e
  const n = (1 + e) * Math.log(1 - t) / Math.log(e);
  return 1.0 - Math.cos(n) * Math.pow(e, n);
  // equivalent to the following?
  // return 1 - elasticIn(1 - t, e);
}

function elasticInOut(t: number, e: number) {
  if (t < 0.5) {
    return 0.5 * elasticIn(2 * t, e);
  }

  return 0.5 + 0.5 * elasticOut(2 * t - 1, e);
  // equivalent to the following?
  // return 0.5 + 0.5 * (1 - elasticIn(2 * (1 - t) - 1, e));
  // return 1 - 0.5 * elasticIn(1 - 2 * t, e);
}

// Bounce, e = elasticity in ]0, +Inf[
function bounceIn(t: number, e: number) {
  if (t === 0) {
    return 0;
  }

  e /= (e + 1); // transforming e
  const n = (1 + e) * Math.log(t) / Math.log(e);
  return Math.abs(Math.cos(n) * Math.pow(e, n));
}

function bounceOut(t: number, e: number) {
  if (t === 1) { return 1; }
  e /= (e + 1); // transforming e
  let n = (1 + e) * Math.log(1 - t) / Math.log(e);
  return 1.0 - Math.abs(Math.cos(n) * Math.pow(e, n));
}

function bounceInOut(t: number, e: number) {
  if (t < 0.5) {
    return 0.5 * bounceIn(2 * t, e);
  }

  return 0.5 + 0.5 * bounceOut(2 * t - 1, e);
}

// Back, e = elasticity in [0, +Inf[
function backIn(t: number, e: number) {
  return t * t * ((e + 1) * t - e);
}

function backOut(t: number, e: number) {
  t -= 1;
  return t * t * ((e + 1) * t + e) + 1;
}

function backInOut(t: number, e: number) {
  if (t < 0.5) {
    return 0.5 * backIn(2 * t, e);
  }
  return 0.5 + 0.5 * backIn(2 * t - 1, e);
}


const ease = {
  none,
  linear,
  flash,
  parabolic,
  trigo,
  polyIn,
  polyOut,
  polyInOut,
  sineIn,
  sineOut,
  sineInOut,
  expIn,
  expOut,
  expInOut,
  circIn,
  circOut,
  circInOut,
  elasticIn,
  elasticOut,
  elasticInOut,
  bounceIn,
  bounceOut,
  bounceInOut,
  backIn,
  backOut,
  backInOut,
};

// ===========================================================
type easeType = (t: number, params?: number) => void;
let defaultEase: easeType = ease.linear;
let defaultEaseOpts = 2;

class Lerp {
  private _t: number;
  public _from: any;
  public _to: any;
  private _duration: number;
  ease: any;
  easeOpts: any;

  constructor (to: number, duration: number = 0) {
    this._t = 0;
    this._to = to;
    this._duration = duration;
    this.ease = defaultEase;
    this.easeOpts = defaultEaseOpts;

    this._from = {};
  }

  init (object: any) { 
    for (let property in this._to) {
      this._from[property] = object[property];
    }
  }

  _updateTime (dt: number) {
    this._t += dt;
    return this._t - this._duration;
  }

  _getEase () {
    return this.ease(Math.min(1, this._t / this._duration), this.easeOpts);
  }

  progress (dt: number, object: any) {
    const overflow = this._updateTime(dt);

    const e = this._getEase();
    for (let property in this._to) {
      object[property] = (1 - e) * this._from[property] + e * this._to[property];
    }

    return overflow;
  }
}


class Merp {
  private _interpolation: any;
  private _interpolationOpts: any;
  
  lerp: Lerp;

  constructor (to: any, duration: number, interpolation: any, interpolationOpts: any) {
    this.lerp = new Lerp(to, duration);

    this._interpolation = interpolation;
    this._interpolationOpts = interpolationOpts;
  }

  init (object: any) { 
    this.lerp.init(object);
  }

  progress (dt: number, object: any) {
    const overflow = this.lerp._updateTime(dt);

    const e = this.lerp._getEase();
    const to = this.lerp._to;
    const from = this.lerp._from;
    for (let property in to) {
      object[property] = this._interpolation(e, from[property], to[property], this._interpolationOpts);
    }

    return overflow;
  }
}

class Callback {
  private _callback: (object: any) => void

  constructor (callback: (object: any) => void) {
    this._callback = callback;
  }

  init () {}

  progress (dt: number, object: any) {
    this._callback(object);
    return dt;
  }
}

class Tempo {
  private _t: number;
  private _duration: number;

  constructor (duration) {
    this._t = 0;
    this._duration = duration;
  }

  init () {}

  progress (dt) {
    this._t += dt;
    return this._t - this._duration;
  }
}

const statuses = {
  none: 0,
  playing: 1,
  adding: 2,
  removing: 3
};

// Unidentified moving object
class UMO {
  static setDefaultEase (ease: easeType, easeOpts: number = 2) {
    defaultEase = ease;
    defaultEaseOpts = easeOpts;
  }

  private _moves: Moves;
  private _object: any;
  private _t: number;
  private _sequence: any[];
  private _lastLerp: Lerp | null;

  status: any;

  constructor (moves: Moves, object: any) {
    this._moves = moves;

    this._object = object;
    this.status = statuses.none;
    this._lastLerp = null;

    this._t = 0;
    this._sequence = [];
  }

  _remove () {
    this._moves._remove(this);
  }

  clear () {
    this._sequence.length = 0;
    this._remove();
    return this;
  }

  _addStep (step: any) {
    if (this._sequence.length === 0) {
      this._moves._add(this);
      step.init(this._object);
    }

    this._sequence.push(step);
  }

  lerp (to: any, duration: number) {
    const lerp = new Lerp(to, duration);
    this._lastLerp = lerp;
    this._addStep(lerp);
    return this;
  }

  merp (to: any, duration: number, interpolation: any, interpolationOpts?: any) {
    const merp = new Merp(to, duration, interpolation, interpolationOpts);
    this._lastLerp = merp.lerp;
    this._addStep(merp);
    return this;
  }

  ease (ease: easeType, easeOpts: number = 1) {
    if (this._lastLerp !== null) {
      this._lastLerp.ease = ease;
      this._lastLerp.easeOpts = easeOpts;
    }
    return this;
  }

  then (callback: (object: any) => void) {
    this._addStep(new Callback(callback));
    return this;
  }

  tempo (duration: number) {
    this._addStep(new Tempo(duration));
    return this;
  }

  promise () {
    return new Promise((resolve) => {
      this._sequence.push(new Callback(resolve));
    });
  }

  _tick (dt: number) {
    while (dt > 0) {
      dt = this._sequence[0].progress(dt, this._object);
      if (dt <= 0) {
        return;
      }

      this._sequence.shift();
      if (this._sequence.length === 0) {
        this._remove();
        return;
      }

      this._sequence[0].init(this._object);
    }
  }
}

class Moves {
  private _uMOsPlaying: Array<UMO>;
  private _uMOsToAdd: Array<UMO>;
  private _uMOsToRemove: Array<UMO>;

  ease = ease

  constructor () {
    this._uMOsPlaying = [];
    this._uMOsToAdd = [];
    this._uMOsToRemove = [];
  }

  _add (uMO: UMO) {
    const status = uMO.status;
    switch (status) {
      case statuses.none:
      case statuses.playing:
        this._uMOsToAdd.push(uMO);
        uMO.status = statuses.adding;
        break;
      case statuses.removing:
        this._uMOsToRemove.splice(this._uMOsToRemove.indexOf(uMO), 1);
        uMO.status = statuses.none;
        break;
      default:
        break;
    }
  }

  _remove (uMO: UMO) {
    const status = uMO.status;
    switch (status) {
      case statuses.playing:
        this._uMOsToRemove.push(uMO);
        uMO.status = statuses.removing;
        break;
      case statuses.adding:
        this._uMOsToAdd.splice(this._uMOsToAdd.indexOf(uMO), 1);
        uMO.status = statuses.none;
        break;
      default:
        break;
    }
  }

  tick (dt: number) {
    // Adding UMOs
    if (this._uMOsToAdd.length > 0) {
      for (let a = 0; a < this._uMOsToAdd.length; a++) {
        const uMOToAdd = this._uMOsToAdd[a];
        this._uMOsPlaying.push(uMOToAdd);
        uMOToAdd.status = statuses.playing;
      }
      this._uMOsToAdd.length = 0;
    }
  
    // Ticking UMOs
    for (let u = 0; u < this._uMOsPlaying.length; u++) {
      this._uMOsPlaying[u]._tick(dt);
    }

    // Removing UMOs
    if (this._uMOsToRemove.length > 0) {
      for (let r = 0; r < this._uMOsToRemove.length; r++) {
        const uMOToRemove = this._uMOsToRemove[r];
        const index = this._uMOsPlaying.indexOf(uMOToRemove);
        if (index !== -1) {
          this._uMOsPlaying.splice(index, 1);
          uMOToRemove.status = statuses.none;
        }
      }
      this._uMOsToRemove.length = 0;
    }
  }

  move (object?: any) {
    return new UMO(this, object);
  }
}

export default Moves;
